import {
    InQueueScenarioFieldsFragment,
    Scenario_Origins_Enum,
    Scenario_Statuses_Enum
} from "generated/graphql";
import { keyBy } from "lodash";
import { Loading } from "types/loadingType";
import {
    castToLegacyClusterInfo,
    LegacyClusterInfo,
    shouldShowInCluster,
    type ScenarioStage
} from "./clusterType";
import {
    InQueueSubscription,
    ProjectType,
    SpeculativeProject,
    TrackedProject
} from "./projectType";

// Re-exports the Hasura-generated status types under a different name
// This enables us to change how this is structured in the future if we
// want to decouple from the Hasura typings.
export type ScenarioStatus = Scenario_Statuses_Enum;
export const ScenarioStatus = Scenario_Statuses_Enum;

export type ScenarioOrigin = Scenario_Origins_Enum;
export const ScenarioOrigin = Scenario_Origins_Enum;

export type ScenarioType = "SHARED" | "CUSTOM";
export type { ScenarioStage } from "./clusterType";

export type ProjectScenarioMapping = {
    [projectId: string]: {
        [phase: string]: ScenarioMetadata[];
    };
};
export interface InputData {
    concise: string;
    description: string;
}
export interface ScenarioMetadata {
    type: ScenarioType;
    origin?: ScenarioOrigin;
    stage: ScenarioStage;
    scenarioId: string;
    title: string;

    clusterInfo: LegacyClusterInfo;

    assumptions: Record<string, MisoPercentageReduction>;
    projectAdditions: SpeculativeProject[];
    customUpgradeId?: string;

    status: ScenarioStatus;
    inputDataUsed?: InputData;
    timeMetadata: ScenarioTimeMetadata;

    isStale: boolean;

    parentProjectId?: string;

    costAssumptions: CostAssumption;

    archived: boolean;
    lastArchivedAt?: Date;
}

export enum CostAssumptionType {
    CONSERVATIVE = "CONSERVATIVE",
    CUSTOM = "CUSTOM",
    NETWORK_UPGRADES = "NETWORK_UPGRADES",
    SUGAR_UPGRADES = "SUGAR_UPGRADES"
}

interface BaseCustomCostAssumption {
    type: CostAssumptionType;
}

export interface CustomCostAssumption extends BaseCustomCostAssumption {
    type: CostAssumptionType.CUSTOM;
    reconductorLoadingLimit: number;
    rebuildLoadingLimit: number;
}

export interface ConservativeCostAssumption extends BaseCustomCostAssumption {
    type: CostAssumptionType.CONSERVATIVE;
}

export interface NetworkUpgradesCostAssumption
    extends BaseCustomCostAssumption {
    type: CostAssumptionType.NETWORK_UPGRADES;
}

export interface SugarCostAssumption extends BaseCustomCostAssumption {
    type: CostAssumptionType.SUGAR_UPGRADES;
}

export type CostAssumption =
    | CustomCostAssumption
    | ConservativeCostAssumption
    | NetworkUpgradesCostAssumption
    | SugarCostAssumption;

export enum ProjectSizeAdjustmentType {
    PERCENTAGE_REDUCTION = "PERCENTAGE_REDUCTION"
}

export interface ProjectSizeAssumptions {
    type: ProjectSizeAdjustmentType;
}

export interface MisoPercentageReduction extends ProjectSizeAssumptions {
    type: ProjectSizeAdjustmentType.PERCENTAGE_REDUCTION;
    eris: number;
    nris: number;
}

export interface ScenarioTimeMetadata {
    createdAt: Date;
    updatedAt: Date;
    runSubmittedAt?: Date;
    runStartedAt?: Date;
    runCompletedAt?: Date;

    latestDate: Date;
}

export type PercentageReductionType =
    typeof ProjectSizeAdjustmentType.PERCENTAGE_REDUCTION;
export type HasuraProjectSizeAdjustment = {
    type: PercentageReductionType;
    eris: number;
    nris: number;
};
export type HasuraProjectSizeAssumptions = {
    [projectId: string]: HasuraProjectSizeAdjustment;
};

type ScenarioMetadataWithMaybeProjectIds = ScenarioMetadata & {
    projectIds?: string[];
};

export const CUSTOM_UPGRADE_ID_KEY = "custom_upgrade_id";

const extractCustomUpgradeId = (
    customUpgrades: unknown
): string | undefined => {
    if (!Array.isArray(customUpgrades) || customUpgrades.length === 0) {
        return undefined;
    }
    const first = customUpgrades[0];
    if (first && typeof first === "object" && CUSTOM_UPGRADE_ID_KEY in first) {
        const id = (first as { [CUSTOM_UPGRADE_ID_KEY]: unknown })[
            CUSTOM_UPGRADE_ID_KEY
        ];
        return typeof id === "string" ? id : undefined;
    }
    return undefined;
};

export const convertHasuraInQueueScenariosToProjectScenarioMapping = (
    hasuraScenarios: ReadonlyArray<InQueueScenarioFieldsFragment>,
    projects: Loading<TrackedProject[]>,
    speculativeProjects: Loading<SpeculativeProject[]>,
    subscriptions: Loading<InQueueSubscription[]>
): Loading<ProjectScenarioMapping> => {
    if (
        projects === "loading" ||
        speculativeProjects === "loading" ||
        subscriptions === "loading"
    ) {
        return "loading";
    }

    const subscriptionIdToTrackedProjectIds: Record<string, string[]> = {};
    projects.forEach((project: TrackedProject) => {
        const subscriptionId = project.subscriptionId;

        if (subscriptionId in subscriptionIdToTrackedProjectIds) {
            subscriptionIdToTrackedProjectIds[subscriptionId].push(
                project.projectId
            );
        } else {
            subscriptionIdToTrackedProjectIds[subscriptionId] = [
                project.projectId
            ];
        }
    });

    const spIdToSpeculativeProject: Record<string, SpeculativeProject> = {};
    speculativeProjects.forEach((sp: SpeculativeProject) => {
        spIdToSpeculativeProject[sp.id] = sp;
    });

    // Preprocess results into ScenarioMetadata
    const scenarios: ScenarioMetadataWithMaybeProjectIds[] = hasuraScenarios
        .map((s) => {
            let maybeProjectIds;
            if (s.scenario_type === "CUSTOM") {
                // Use parent project ID if present, otherwise use tracked projects
                if (s.parent_project_id) {
                    maybeProjectIds = [s.parent_project_id];
                } else if (s.parent_speculative_project_id) {
                    maybeProjectIds = [
                        spIdToSpeculativeProject[
                            s.parent_speculative_project_id
                        ].projectId
                    ];
                } else {
                    maybeProjectIds =
                        subscriptionIdToTrackedProjectIds[s.subscription_id];
                }
            }
            // Future: Query for the cluster once we update schema.
            // linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
            const clusterInfo = castToLegacyClusterInfo({
                region: s.region,
                studyCycle: s.study_cycle,
                studyGroup: s.study_group,
                studyPhase: s.study_phase
            });
            if (!clusterInfo) {
                return null;
            }

            return {
                scenarioId: s.scenario_id,
                type: s.scenario_type as ScenarioType,
                origin: s.scenario_origin ?? undefined,
                stage: s.scenario_stage as ScenarioStage,
                title: s.title,
                assumptions: s.project_size_assumptions,
                projectAdditions: s.project_additions.map(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (pa: any) =>
                        spIdToSpeculativeProject[pa.speculative_project_id]
                ),
                customUpgradeId: extractCustomUpgradeId(s.custom_upgrades),

                clusterInfo,

                inputDataUsed: s.input_data_used,
                status: s.status,
                timeMetadata: convertHasuraScenarioToScenarioRunMetadata(s),
                isStale: s.is_stale ?? false,

                projectIds: maybeProjectIds,
                parentProjectId:
                    (s.parent_project_id ||
                        spIdToSpeculativeProject[
                            s.parent_speculative_project_id
                        ]?.projectId) ??
                    undefined,

                costAssumptions: s.cost_assumptions,
                archived: s.is_archived ?? false,
                lastArchivedAt: s.last_archived_at
                    ? new Date(s.last_archived_at)
                    : undefined
            };
        })
        .filter((s) => s !== null) as ScenarioMetadataWithMaybeProjectIds[];

    // Iterate over projects and find matching scenarios
    const result: ProjectScenarioMapping = {};
    const subscriptionsById = keyBy(
        subscriptions,
        (s: InQueueSubscription) => s.subscriptionId
    );
    projects.forEach((project: TrackedProject) => {
        const projectId = project.projectId;
        const subscription = subscriptionsById[project.subscriptionId];

        let filteredScenarios = scenarios.filter((s) =>
            shouldShowInCluster(
                s.clusterInfo,
                subscription?.clusterInfo ?? project.cluster
            )
        );

        if (project.type === ProjectType.SPECULATIVE_PROJECT) {
            // If it's a speculative project, scenarios that include this project in the projectAdditions
            filteredScenarios = filteredScenarios.filter((s) =>
                s.projectAdditions.find((pa) => pa.projectId === projectId)
            );
        }

        const relevantSharedScenarios = filteredScenarios.filter(
            (s) => s.type === "SHARED"
        );
        const relevantCustomScenarios = filteredScenarios.filter(
            (s) => s.type === "CUSTOM" && s.projectIds?.includes(projectId)
        );

        const projectScenarios = [
            ...relevantSharedScenarios,
            ...relevantCustomScenarios
        ];

        result[projectId] = {
            ...result[projectId],
            [project.cluster.studyPhase]: projectScenarios
        };
    });

    return result;
};

const convertHasuraScenarioToScenarioRunMetadata = (input: {
    created_at: string;
    updated_at: string;
    run_submitted_at?: string;
    run_started_at?: string;
    run_completed_at?: string;
}): ScenarioTimeMetadata => {
    const createdAt = new Date(input.created_at);
    const runSubmittedAt = input.run_submitted_at
        ? new Date(input.run_submitted_at)
        : undefined;
    const runStartedAt = input.run_started_at
        ? new Date(input.run_started_at)
        : undefined;
    const runCompletedAt = input.run_completed_at
        ? new Date(input.run_completed_at)
        : undefined;
    return {
        createdAt,
        updatedAt: new Date(input.updated_at),
        runSubmittedAt,
        runStartedAt,
        runCompletedAt,
        latestDate:
            runCompletedAt ?? runStartedAt ?? runSubmittedAt ?? createdAt
    };
};

export enum ScenarioAssumptionsCSVFields {
    project_id = "project_id",
    eris_percentage_reduction = "eris_percentage_reduction",
    nris_percentage_reduction = "nris_percentage_reduction",
    fuel_type = "fuel_type (reference)",
    energy_mw = "energy_mw (reference)",
    capacity_mw = "capacity_mw (reference)",
    status = "status (reference)",
    is_withdrawn = "is_withdrawn (reference)",
    study_cycle = "study_cycle (reference)",
    study_group = "study_group (reference)",
    base_cost_per_mw = "base_cost_per_mw (reference)",
    base_total_cost = "base_total_cost (reference)",
    base_upgrades = "base_upgrades (reference)",
    base_rank = "base_rank (reference)",
    base_percentile = "base_percentile (reference)"
}

export interface ScenarioAssumptionsCSV {
    [ScenarioAssumptionsCSVFields.project_id]: string;
    [ScenarioAssumptionsCSVFields.fuel_type]?: string;
    [ScenarioAssumptionsCSVFields.energy_mw]?: number | "";
    [ScenarioAssumptionsCSVFields.capacity_mw]?: number | "";
    [ScenarioAssumptionsCSVFields.status]?: string;
    [ScenarioAssumptionsCSVFields.is_withdrawn]?: boolean | "";
    [ScenarioAssumptionsCSVFields.study_cycle]?: string;
    [ScenarioAssumptionsCSVFields.study_group]?: string;
    [ScenarioAssumptionsCSVFields.base_cost_per_mw]?: number | "";
    [ScenarioAssumptionsCSVFields.base_total_cost]?: number | "";
    [ScenarioAssumptionsCSVFields.base_upgrades]?: number | "";
    [ScenarioAssumptionsCSVFields.base_rank]?: number | "";
    [ScenarioAssumptionsCSVFields.base_percentile]?: number | "";
    [ScenarioAssumptionsCSVFields.eris_percentage_reduction]: number | "";
    [ScenarioAssumptionsCSVFields.nris_percentage_reduction]: number | "";
}
